/**
 * Préstamos Saldados — migración web de ares3050.
 *
 * Préstamos saldados filtrados por rango sobre la FECHA DE INICIO del
 * préstamo y por cliente (nombre o cédula). Criterio corregido respecto
 * al escritorio: se considera saldado cuando el saldo real es <= 1
 * incluyendo descuentos y abonos a capital (descinte, abonoacapital,
 * interesdescontado) — el escritorio los omitía y dejaba fuera
 * préstamos saldados con ajustes. Exporta a Excel e imprime.
 */
import React, { useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioAnioISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales });
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function PrestamosSaldados() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioAnioISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [cliente, setCliente] = useState("");
  // Financiamientos de motores (General) o préstamos personales
  // (prestamo.ppersonal): mismo flag que el informe de pagos.
  const [prestamosPersonales, setPrestamosPersonales] = useState(false);

  const [prestamos, setPrestamos] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/prestamossaldados", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        cliente: cliente.trim(),
        prestamospersonales: prestamosPersonales,
      });
      if (ok) {
        setPrestamos(data.prestamos || []);
        setTotales(data.totales || null);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitulo = `Iniciados del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}${cliente ? ` · ${cliente}` : ""}`;

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Préstamos Saldados"],
      [subtitulo],
      [],
      [
        "Préstamo",
        "Fecha",
        "Cédula",
        "Nombres",
        "Solicitado",
        "Pagado",
        "Ajustes",
        "Cancelado",
        "Sucursal",
      ],
      ...prestamos.map((p) => [
        p.noprest,
        p.fecha,
        p.cedula,
        p.nombres,
        p.cantsol,
        p.pagado,
        p.ajustes,
        p.fcancel,
        p.sucursal,
      ]),
      [],
      ["", "", "", "TOTAL", totales?.monto || 0, totales?.pagado || 0, "", "", ""],
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Saldados");
    XLSX.writeFile(libro, `PrestamosSaldados_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    imprimirListado(
      "Préstamos Saldados",
      subtitulo,
      [
        "Préstamo",
        "Fecha",
        "Cédula",
        "Nombres",
        "Solicitado",
        "Pagado",
        "Ajustes",
        "Cancelado",
        "Sucursal",
      ],
      prestamos.map((p) => [
        p.noprest,
        p.fecha,
        p.cedula,
        p.nombres,
        fmtMoney(p.cantsol),
        fmtMoney(p.pagado),
        fmtMoney(p.ajustes),
        p.fcancel || "",
        p.sucursal,
      ]),
      ["", "", "", "TOTAL", fmtMoney(totales?.monto), fmtMoney(totales?.pagado), "", "", ""]
    );
  };

  const columnas = [
    { field: "noprest", headerName: "Préstamo", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 100 },
    { field: "cedula", headerName: "Cédula", width: 120 },
    { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 180 },
    moneyCol("cantsol", "Solicitado"),
    moneyCol("pagado", "Pagado"),
    moneyCol("ajustes", "Ajustes"),
    { field: "fcancel", headerName: "Cancelado", width: 105 },
    { field: "sucursal", headerName: "Sucursal", width: 100 },
  ];

  const hayDatos = prestamos.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos Saldados"
        subtitle="Préstamos saldados por rango de fecha de inicio, incluyendo descuentos y abonos a capital"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Cliente (nombre o cédula, opcional)"
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            {/* General = financiamientos de motores; Personal = préstamos
                personales (mismo corte que el informe de pagos). */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo de préstamo"
                value={prestamosPersonales ? "personal" : "general"}
                onChange={(e) => setPrestamosPersonales(e.target.value === "personal")}
                sx={fpInputSx}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="general">General</option>
                <option value="personal">Personal</option>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="CANTIDAD" value={totales?.cantidad ?? 0} />
            <ResumenChip label="MONTO" value={`RD$ ${fmtMoney(totales?.monto)}`} color="#2E7D32" />
            <ResumenChip
              label="PAGADO"
              value={`RD$ ${fmtMoney(totales?.pagado)}`}
              color="#EF6C00"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={prestamos}
              columns={columnas}
              getRowId={(r) => r.noprest}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
